import { Button, Group, Loader, Progress, Text, Tooltip } from "@mantine/core";
import { IconReceipt, IconRefresh } from "@tabler/icons-react";
import { displayLastUpdated } from "../../utils/display";

interface InvoiceRefreshCardProps {
  organization: { last_accounting_data_download?: string; accounting_software?: string | null } | null | undefined;
  refreshInvoicesMutation: {
    isPending: boolean;
  };
  refreshCustomersMutation: {
    isPending: boolean;
  };
  isRefreshing: boolean;
  onRefreshInvoices: () => void;
  onRefreshCustomers: () => void;
}

export function InvoiceRefreshCard({
  organization,
  refreshInvoicesMutation,
  refreshCustomersMutation,
  isRefreshing,
  onRefreshInvoices,
  onRefreshCustomers,
}: InvoiceRefreshCardProps) {
  const showInProgress =
    refreshInvoicesMutation.isPending ||
    refreshCustomersMutation.isPending ||
    isRefreshing;
  const supportsCustomerOnlySync = organization?.accounting_software === "Priority";

  return (
    <div className="rounded-xl border border-slate-200 bg-linear-to-br from-blue-50/80 to-sky-50/50 p-5">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-blue-500 to-sky-500 text-white shadow-sm">
          <IconReceipt size={22} stroke={2} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">
            Refresh invoice data
          </h3>
          <p className="text-sm text-slate-600">
            Sync latest data from your accounting software
          </p>
        </div>
      </div>
      <div className="mb-4">
        <Text size="sm" c="gray.7" className="font-medium">
          Last updated:{" "}
          <span className="font-semibold text-slate-800">
            {displayLastUpdated(
              organization?.last_accounting_data_download || "",
            )}
          </span>
        </Text>
      </div>
      {showInProgress && (
        <div className="mb-4 rounded-lg border border-blue-100 bg-white/90 p-3">
          <Text size="sm" fw={500} c="blue.8" className="mb-2">
            Refresh queued. Invoice data usually updates within a few minutes.
          </Text>
          <Progress size="sm" radius="xl" color="blue" value={100} animated />
        </div>
      )}
      <Group justify="flex-end" gap="sm">
        {supportsCustomerOnlySync && (
          <Tooltip label="Sync customer organizations and contacts only (no invoices)">
            <Button
              leftSection={
                showInProgress ? (
                  <Loader size="xs" color="blue" />
                ) : (
                  <IconRefresh size={18} />
                )
              }
              onClick={onRefreshCustomers}
              disabled={showInProgress}
              variant="light"
              color="blue"
              size="md"
              radius="md"
            >
              {showInProgress ? "Syncing..." : "Sync Customers Only"}
            </Button>
          </Tooltip>
        )}
        <Tooltip label="Refresh invoices from accounting software">
          <Button
            leftSection={
              showInProgress ? (
                <Loader size="xs" color="white" />
              ) : (
                <IconRefresh size={18} />
              )
            }
            onClick={onRefreshInvoices}
            disabled={showInProgress}
            variant="filled"
            color="blue"
            size="md"
            radius="md"
          >
            {showInProgress ? "Refreshing..." : "Refresh Invoices"}
          </Button>
        </Tooltip>
      </Group>
    </div>
  );
}
